import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { MODULE_BUILDER_STEPS } from "../../constants/module";
import { validateModule } from "../../utils/validation/moduleValidation";
import {
  setCurrentStep,
  setModuleValidation,
  clearModuleData,
} from "../../store/features/moduleSlice";
import { ModuleBasicsStep } from "./steps/ModuleBasicsStep";
import { ModulePowerStep } from "./steps/ModulePowerStep";
import { ModuleComputerStep } from "./steps/ModuleComputerStep";
import { ModuleSensorsStep } from "./steps/ModuleSensorsStep";
import { ModuleDrivesStep } from "./steps/ModuleDrivesStep";
import { ModuleFuelStep } from "./steps/ModuleFuelStep";
import { ModuleWeaponsStep } from "./steps/ModuleWeaponsStep";
import { ModuleSystemsStep } from "./steps/ModuleSystemsStep";
import { ModuleCargoStep } from "./steps/ModuleCargoStep";
import { ModuleCalculationsStep } from "./steps/ModuleCalculationsStep";
import { ModuleExportStep } from "./steps/ModuleExportStep";
import { ModuleNavigation } from "./ModuleNavigation";
import { ErrorBoundary } from "./components/ErrorBoundary";

/**
 * Main Module Builder Component
 * Provides a dedicated interface for building modules
 */
export const ModuleBuilder: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleState = useAppSelector((state) => state.module);
  const currentStep = moduleState.currentStep;
  const moduleConfig = moduleState.moduleConfig;

  // Get current step data
  const currentStepData = MODULE_BUILDER_STEPS[currentStep];

  // Validate module on state changes
  useEffect(() => {
    const validation = validateModule({
      moduleConfig,
      currentStep: currentStepData?.id || "",
    });
    dispatch(setModuleValidation(validation));
  }, [moduleConfig, currentStep, dispatch, currentStepData]);

  // Initialize module data if not present
  useEffect(() => {
    if (!moduleConfig) {
      // Initialize with default module configuration
      // This will be handled by the ModuleBasicsStep
    }
  }, [moduleConfig]);

  /**
   * Handles moving to the next step
   */
  const handleNext = () => {
    if (currentStep < MODULE_BUILDER_STEPS.length - 1) {
      dispatch(setCurrentStep(currentStep + 1));
    }
  };

  /**
   * Handles moving to the previous step
   */
  const handlePrevious = () => {
    if (currentStep > 0) {
      dispatch(setCurrentStep(currentStep - 1));
    }
  };

  /**
   * Handles resetting the module builder
   */
  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset the module design? This will clear all current progress.")) {
      dispatch(clearModuleData());
    }
  };

  /**
   * Renders the appropriate step component
   */
  const renderStepContent = () => {
    if (!currentStepData) {
      return (
        <div className="text-center text-red-600 dark:text-red-400">
          Error: Invalid step configuration
        </div>
      );
    }

    switch (currentStepData.id) {
      case "module-basics":
        return <ModuleBasicsStep />;
      case "module-power":
        return <ModulePowerStep />;
      case "module-computer":
        return <ModuleComputerStep />;
      case "module-sensors":
        return <ModuleSensorsStep />;
      case "module-drives":
        return <ModuleDrivesStep />;
      case "module-fuel":
        return <ModuleFuelStep />;
      case "module-weapons":
        return <ModuleWeaponsStep />;
      case "module-systems":
        return <ModuleSystemsStep />;
      case "module-cargo":
        return <ModuleCargoStep />;
      case "module-calculations":
        return <ModuleCalculationsStep />;
      case "module-export":
        return <ModuleExportStep />;
      default:
        return (
          <div className="text-center text-red-600 dark:text-red-400">
            Unknown step: {currentStepData.id}
          </div>
        );
    }
  };

  // Show loading state if no step data
  if (!currentStepData) {
    return (
      <div className="container mx-auto px-2 md:px-6 lg:px-12 max-w-screen-xl">
        <div className="w-full flex flex-col items-center mb-2">
          <h1
            className="text-4xl font-extrabold text-highlightOrange tracking-wide text-center"
            style={{ fontFamily: "Orbitron, sans-serif" }}
          >
            Module Builder
          </h1>
        </div>
        <div className="text-center text-red-600 dark:text-red-400">
          Loading module configuration...
        </div>
      </div>
    );
  }

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === MODULE_BUILDER_STEPS.length - 1;
  const canProceed = moduleState.validation.isValid;

  return (
    <div className="container mx-auto px-2 md:px-6 lg:px-12 max-w-screen-xl">
      {/* Header */}
      <div className="w-full flex flex-col items-center mb-2">
        <h1
          className="text-4xl font-extrabold text-highlightOrange tracking-wide text-center"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          Module Builder
        </h1>
        <div className="text-center mt-2">
          <span className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm font-semibold px-3 py-1 rounded-full border border-red-200 dark:border-red-700">
            BETA TEST Version 0.1.4
          </span>
        </div>
        <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">
          Design modular components for installation in modular hulls
        </p>
      </div>

      {/* Navigation */}
      <ModuleNavigation
        currentStep={currentStep}
        steps={MODULE_BUILDER_STEPS}
        onStepChange={(step) => dispatch(setCurrentStep(step))}
        validation={moduleState.validation}
      />

      {/* Step Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
            {currentStepData.label}
          </h2>
          <p className="text-gray-600 dark:text-gray-300">{currentStepData.description}</p>
        </div>

        <ErrorBoundary>
          {renderStepContent()}
        </ErrorBoundary>
      </div>

      {/* Validation Messages */}
      {moduleState.validation.errors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
            Validation Errors
          </h3>
          <ul className="list-disc list-inside text-red-700 dark:text-red-300">
            {moduleState.validation.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {moduleState.validation.warnings.length > 0 && (
        <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
            Warnings
          </h3>
          <ul className="list-disc list-inside text-yellow-700 dark:text-yellow-300">
            {moduleState.validation.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mb-32">
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-500 dark:bg-gray-600 text-white rounded hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
          >
            Reset Module
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handlePrevious}
            disabled={isFirstStep}
            className={`px-4 py-2 rounded transition-colors ${
              isFirstStep
                ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Previous
          </button>

          <button
            onClick={handleNext}
            disabled={isLastStep || !canProceed}
            className={`px-4 py-2 rounded transition-colors ${
              isLastStep || !canProceed
                ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                : "bg-highlightOrange text-black hover:bg-orange-600"
            }`}
          >
            {isLastStep ? "Complete" : "Next"}
          </button>
        </div>
      </div>


    </div>
  );
};
